/**
 * Beta License Validator
 * Validates beta testing license keys locally without API calls
 * Beta keys format: BETA-{userId}-{expiryTimestamp}-{signature}
 */

import type {
  LicenseInfo,
  LicenseValidationResult,
  LicenseActivationResult,
  OfflineGracePeriodStatus,
  DeviceInfo,
  LicenseValidationError
} from '$lib/types/subscription';

import type {
  LicenseValidator,
  SecureLicenseStorage,
  DeviceLimitManager,
  OfflineGracePeriodManager,
  LicenseProgressCallback
} from './interfaces';

import { LICENSE_CONFIG } from '$lib/constants/subscription';

/**
 * Beta License Validator
 * Handles local validation of beta testing licenses
 */
export class BetaLicenseValidator implements LicenseValidator {
  private readonly storage: SecureLicenseStorage;
  private readonly deviceManager: DeviceLimitManager;
  private readonly gracePeriodManager: OfflineGracePeriodManager;

  constructor(
    storage: SecureLicenseStorage,
    deviceManager: DeviceLimitManager,
    gracePeriodManager: OfflineGracePeriodManager
  ) {
    this.storage = storage;
    this.deviceManager = deviceManager;
    this.gracePeriodManager = gracePeriodManager;
  }

  /**
   * Check if a license key is a beta key
   */
  static isBetaKey(licenseKey: string): boolean {
    return licenseKey.startsWith('BETA-');
  }

  /**
   * Validate a beta license key
   */
  async validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
    try {
      // Check if it's a beta key
      if (!BetaLicenseValidator.isBetaKey(licenseKey)) {
        return this.createErrorResult('invalid_format', 'Not a beta license key');
      }

      // Parse the beta key
      const parsed = this.parseBetaKey(licenseKey);
      if (!parsed.valid) {
        return this.createErrorResult('invalid_format', parsed.error || 'Invalid beta key format');
      }

      // Check if expired
      if (parsed.expiresAt && new Date() > parsed.expiresAt) {
        return this.createErrorResult('expired_license', 'Beta license has expired');
      }

      // Create license info
      const currentDevice = await this.deviceManager.getCurrentDeviceInfo();
      const licenseInfo: LicenseInfo = {
        licenseKey,
        provider: 'manual',
        productId: 'beta-program',
        productName: 'Korean Grammar Checker Beta',
        email: parsed.userId,
        purchaseDate: new Date(), // Beta start date
        expiresAt: parsed.expiresAt,
        uses: 0,
        maxUses: undefined,
        devices: [currentDevice],
        maxDevices: LICENSE_CONFIG.MAX_DEVICES,
        isActive: true,
        lastValidatedAt: new Date(),
        metadata: {
          type: 'beta',
          userId: parsed.userId,
          isBetaTester: true
        }
      };

      // Store license
      await this.storage.storeLicense(licenseKey, licenseInfo);

      return {
        isValid: true,
        license: licenseInfo,
        fromCache: false,
        validatedAt: new Date()
      };
    } catch (error) {
      return this.createErrorResult('validation_failed', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Parse beta key format: BETA-{userId}-{expiryTimestamp}-{signature}
   */
  private parseBetaKey(licenseKey: string): {
    valid: boolean;
    userId?: string;
    expiresAt?: Date;
    signature?: string;
    error?: string;
  } {
    try {
      const parts = licenseKey.split('-');

      // Must have at least 3 parts: BETA, userId, and either timestamp or signature
      if (parts.length < 3) {
        return { valid: false, error: 'Invalid beta key format' };
      }

      const [prefix, userId, ...rest] = parts;

      if (prefix !== 'BETA') {
        return { valid: false, error: 'Invalid prefix' };
      }

      if (!userId) {
        return { valid: false, error: 'Missing user ID' };
      }

      // Try to parse expiry timestamp (optional)
      let expiresAt: Date | undefined;
      let signature: string | undefined;

      if (rest.length >= 2) {
        // Format: BETA-userId-timestamp-signature
        const timestampStr = rest[0];
        signature = rest[1];

        // Try to parse as Unix timestamp (seconds) or YYYYMMDD
        if (timestampStr.length === 8) {
          // YYYYMMDD format
          const year = parseInt(timestampStr.substring(0, 4));
          const month = parseInt(timestampStr.substring(4, 6)) - 1;
          const day = parseInt(timestampStr.substring(6, 8));
          expiresAt = new Date(year, month, day);
        } else {
          // Try Unix timestamp
          const timestamp = parseInt(timestampStr);
          if (!isNaN(timestamp)) {
            expiresAt = new Date(timestamp * 1000);
          }
        }
      } else if (rest.length === 1) {
        // Format: BETA-userId-signature (no expiry)
        signature = rest[0];
      }

      // Validate signature format (alphanumeric, at least 6 chars)
      if (signature && (signature.length < 6 || !/^[A-Z0-9]+$/i.test(signature))) {
        return { valid: false, error: 'Invalid signature format' };
      }

      return {
        valid: true,
        userId,
        expiresAt,
        signature
      };
    } catch (error) {
      return { valid: false, error: 'Failed to parse beta key' };
    }
  }

  /**
   * Activate beta license on device
   */
  async activateLicense(
    licenseKey: string,
    deviceInfo: DeviceInfo,
    progressCallback?: LicenseProgressCallback
  ): Promise<LicenseActivationResult> {
    try {
      progressCallback?.({ stage: 'validation', message: 'Validating beta license...', progress: 10 });

      // Validate the license
      const validationResult = await this.validateLicense(licenseKey);
      if (!validationResult.isValid || !validationResult.license) {
        return {
          success: false,
          error: validationResult.error || this.createError('invalid_license', 'Beta license validation failed')
        };
      }

      progressCallback?.({ stage: 'validation', message: 'Beta license validated', progress: 30 });

      // Check device limits
      progressCallback?.({ stage: 'activation', message: 'Checking device limits...', progress: 50 });

      const canActivate = await this.canActivateOnDevice(licenseKey, deviceInfo);
      if (!canActivate) {
        return {
          success: false,
          requiresDeviceDeactivation: true,
          error: this.createError('device_limit_exceeded', 'Maximum device limit reached'),
          deviceInfo
        };
      }

      // Add device
      progressCallback?.({ stage: 'activation', message: 'Activating on device...', progress: 70 });

      const deviceAdded = await this.deviceManager.addDevice(licenseKey, deviceInfo);
      if (!deviceAdded) {
        return {
          success: false,
          error: this.createError('validation_failed', 'Failed to add device')
        };
      }

      // Store license
      progressCallback?.({ stage: 'storage', message: 'Storing license data...', progress: 90 });
      await this.storage.storeLicense(licenseKey, validationResult.license);

      progressCallback?.({ stage: 'cleanup', message: 'Activation complete', progress: 100 });

      return {
        success: true,
        license: validationResult.license,
        deviceInfo
      };
    } catch (error) {
      return {
        success: false,
        error: this.createError('unknown_error', error instanceof Error ? error.message : 'Activation failed')
      };
    }
  }

  /**
   * Deactivate beta license on device
   */
  async deactivateLicense(licenseKey: string, deviceId?: string): Promise<boolean> {
    try {
      const targetDeviceId = deviceId || (await this.deviceManager.getCurrentDeviceInfo()).deviceId;

      // Remove from storage
      await this.storage.removeLicense(licenseKey);
      await this.deviceManager.removeDevice(licenseKey, targetDeviceId);

      return true;
    } catch (error) {
      console.error('Failed to deactivate beta license:', error);
      return false;
    }
  }

  /**
   * Check if beta license can be validated offline (always true for beta)
   */
  async canValidateOffline(licenseKey: string): Promise<boolean> {
    return await this.storage.hasLicense(licenseKey);
  }

  /**
   * Get offline grace period status (beta licenses don't need grace period)
   */
  async getOfflineGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    // Beta licenses are always valid offline until expiry
    return {
      isInGracePeriod: true,
      lastValidationAt: new Date(),
      gracePeriodExpiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      daysRemaining: 365
    };
  }

  /**
   * Refresh beta license cache
   */
  async refreshLicenseCache(licenseKey: string): Promise<LicenseValidationResult> {
    // Re-validate the license
    return await this.validateLicense(licenseKey);
  }

  /**
   * Get device information for beta license
   */
  async getDeviceInfo(licenseKey: string): Promise<DeviceInfo[]> {
    return await this.deviceManager.getActiveDevices(licenseKey);
  }

  /**
   * Check if device can be activated
   */
  async canActivateOnDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    const cachedLicense = await this.storage.getLicense(licenseKey);
    if (!cachedLicense) {
      // First activation - always allow
      return true;
    }

    return await this.deviceManager.canAddDevice(licenseKey, cachedLicense.maxDevices);
  }

  /**
   * Helper methods
   */

  private createErrorResult(type: string, message: string): LicenseValidationResult {
    return {
      isValid: false,
      error: this.createError(type as any, message),
      validatedAt: new Date()
    };
  }

  private createError(type: string, message: string): LicenseValidationError {
    return {
      type: type as any,
      message,
      retryable: false,
      details: {}
    };
  }
}